import {
  IresDeviceIpuState,
  IresDevice,
  getIresDeviceStatus,
  isActiveProgramChainOfIresDevice,
  getIresDeviceIpuState,
  getIresDeviceCategory,
  IresDeviceCategory,
} from "./IresDevice";
import ClientPanel from "./ClientPanel";
import { ProgramChain, getEndDateOfProgramChain } from "./ProgramChain";
import { getMinutesSince } from "@/utils/DateHelper";
import User from "./User";
import {
  DeviceFilter,
  IresDeviceFilterContext,
  matchesIresDeviceFilterWithContext,
  resolveIresDeviceFilterContext,
} from "@/data/models/DeviceFilter";
import { ComBox } from "./ComBox";

export enum DeviceSortOrder {
  DRYING_START,
  STATUS,
  PROJECT,
  SERIAL_NUMBER,
  INVENTORY_NUMBER,
}

export enum SortTimestampSource {
  ACTIVE_DEVICE,
  DEVICE_IN_USAGE,
  COMPLETED_PROGRAM_CHAIN,
  LAST_INFO,
  NO_INFO,
}

export interface DeviceSortData {
  iresDevice: IresDevice;
  serialNumber: string;
  inventoryNumber?: string;
  iresDeviceIpuState: IresDeviceIpuState;
  sortTimestamp: number;
  sortTimestampSource: SortTimestampSource;
  currentProjectName?: string;
}

/**
 * One collator shared by all device list sorting.
 *
 * `String.prototype.localeCompare` builds a collator on every call, which dominates
 * the run time of sorting a master client's ~1000 devices (~10^4 comparisons, and
 * the previous implementation built two collators per comparison whether the sort
 * order used them or not). A cached `Intl.Collator` orders identically to the
 * argument-less `localeCompare` it replaces.
 */
const deviceCollator = new Intl.Collator();

/**
 * A device ready to be sorted: its sort keys, plus whether it counts as in usage.
 *
 * `isInUsage` is resolved once per device before sorting rather than inside the
 * comparator, where it used to cost two `matchesIresDeviceFilter` calls per
 * comparison, each re-parsing the whole user and client documents out of session
 * storage. It also removes a correctness hazard: that check reads the wall clock, so
 * a device's key could change mid-sort and leave the result in no order at all.
 */
export interface ComparableIresDevice {
  sortData?: DeviceSortData;
  isInUsage: boolean;
}

/**
 * Compares two devices for the given sort order.
 *
 * Pure: everything it needs is on the two arguments, so it reads no store, no
 * storage and no clock. `applyInUsageSplit` is the caller's decision (only the
 * unfiltered "Alle Geräte" view splits by usage) and is resolved once per sort.
 *
 * Devices with no sort data sort last: their keys have not been seeded yet, and they
 * must not lead the list.
 */
export function compareBySortOption(
  sortOrder: DeviceSortOrder,
  applyInUsageSplit: boolean,
  first: ComparableIresDevice,
  second: ComparableIresDevice
): number {
  const deviceSortData1 = first.sortData;
  const deviceSortData2 = second.sortData;

  if (!deviceSortData1 && !deviceSortData2) {
    return 0;
  } else if (!deviceSortData1) {
    return 1;
  } else if (!deviceSortData2) {
    return -1;
  }

  const compareSerialNumbers = () =>
    deviceCollator.compare(
      deviceSortData1.serialNumber,
      deviceSortData2.serialNumber
    );

  const compareInventoryNumbers = () =>
    deviceCollator.compare(
      deviceSortData1.inventoryNumber ?? deviceSortData1.serialNumber,
      deviceSortData2.inventoryNumber ?? deviceSortData2.serialNumber
    );

  switch (sortOrder) {
    case DeviceSortOrder.PROJECT: {
      const project1Name = deviceSortData1.currentProjectName;
      const project2Name = deviceSortData2.currentProjectName;

      if (applyInUsageSplit && second.isInUsage !== first.isInUsage) {
        // Devices that are NOT in usage lead the list in this view.
        return second.isInUsage ? -1 : 1;
      }

      if (!project1Name && !project2Name) {
        // Both devices have no project name, fallback to inventory number comparison
        return compareInventoryNumbers();
      } else if (!project1Name) {
        // Only deviceSortData1 has no project name, it should come first
        return -1;
      } else if (!project2Name) {
        // Only deviceSortData2 has no project name, it should come first
        return 1;
      } else {
        // Both devices have project names, compare them
        const projectNameCmp = deviceCollator.compare(project1Name, project2Name);

        if (projectNameCmp !== 0) {
          return projectNameCmp;
        } else {
          return deviceSortData1.sortTimestamp - deviceSortData2.sortTimestamp;
        }
      }
    }
    case DeviceSortOrder.DRYING_START: {
      const sortTimestampSource1 = deviceSortData1.sortTimestampSource;
      const sortTimestampSource2 = deviceSortData2.sortTimestampSource;

      if (sortTimestampSource1 !== sortTimestampSource2) {
        return sortTimestampSource1 - sortTimestampSource2;
      }

      const runningSince1 = deviceSortData1.sortTimestamp;
      const runningSince2 = deviceSortData2.sortTimestamp;

      if (runningSince1 == runningSince2) {
        return compareSerialNumbers();
      } else {
        return runningSince1 - runningSince2;
      }
    }
    case DeviceSortOrder.SERIAL_NUMBER: {
      return compareSerialNumbers();
    }
    case DeviceSortOrder.INVENTORY_NUMBER: {
      return compareInventoryNumbers();
    }
    case DeviceSortOrder.STATUS: {
      const statusCmp =
        deviceSortData1.iresDeviceIpuState - deviceSortData2.iresDeviceIpuState;

      if (statusCmp === 0) {
        return compareSerialNumbers();
      } else {
        return statusCmp;
      }
    }
  }
}

export function getSortOrderName(vue: Vue, deviceSortOrder: DeviceSortOrder) {
  switch (deviceSortOrder) {
    case DeviceSortOrder.PROJECT:
      return vue.$t("panelList.sortByProject").toString();
    case DeviceSortOrder.DRYING_START:
      return vue.$t("panelList.sortByDryingStart").toString();
    case DeviceSortOrder.STATUS:
      return vue.$t("panelList.sortByStatus").toString();
    case DeviceSortOrder.SERIAL_NUMBER:
      return vue.$t("panelList.sortBySerialnumber").toString();
    case DeviceSortOrder.INVENTORY_NUMBER:
      return vue.$t("panelList.sortByInventoryNumber").toString();
  }
}

export function getProjectInfoOfIresDevice(
  iresDevice: IresDevice,
  clientPanel: ClientPanel | null
) {
  const currentAssignment = iresDevice.currentAssignment;

  if (
    currentAssignment &&
    currentAssignment.currentProjectId &&
    currentAssignment.currentProjectId != null
  ) {
    const projectName = currentAssignment.currentProjectName ?? undefined
    const projectNumber = currentAssignment.currentProjectNumber ??
        clientPanel?.currentProjectNumber ??
        undefined

    return {
      currentProjectAssignmentTimeRange:
        currentAssignment.currentProjectAssignmentTimeRange,
      projectName: projectName,
      projectNumber: projectName === projectNumber ? undefined : projectNumber
        ,
      projectId: currentAssignment.currentProjectId,
    };
  }

  if (!clientPanel?.currentProjectId) {
    return null;
  }

  return {
    currentProjectAssignmentTimeRange:
      clientPanel.currentProjectAssignmentTimeRange,
    projectName: clientPanel.currentProjectName,
    projectNumber: clientPanel.currentProjectNumber,
    projectId: clientPanel.currentProjectId,
  };
}

export function getLastProjectInfoOfIresDevice(
  clientPanel: ClientPanel | null
) {
  if (!clientPanel) {
    return null;
  }

  const lastProjectId = clientPanel.lastProjectId;

  if (!lastProjectId) {
    return null;
  }

  const projectName = clientPanel.lastProjectName ?? undefined;
  const projectNumber = clientPanel.lastProjectNumber ?? undefined;

  return {
    currentProjectAssignmentTimeRange:
      clientPanel.lastProjectAssignmentTimeRange,
    projectName: projectName,
    projectNumber: projectName === projectNumber ? undefined : projectNumber,
    projectId: lastProjectId,
  };
}

/**
 * Derives the sort keys of one device.
 *
 * Pass `filterContext` when seeding a whole list: without it the two device-filter
 * checks below each re-parse the user and client documents out of session storage.
 */
export function calculateIresDeviceSortData(
  iresDevice: IresDevice,
  activeUser: User | null,
  clientPanel: ClientPanel | null,
  programChain: ProgramChain | null,
  filterContext?: IresDeviceFilterContext
) {
  const context = filterContext ?? resolveIresDeviceFilterContext(activeUser);
  const deviceStatus = getIresDeviceStatus(iresDevice);

  let sortTimestamp = deviceStatus
    ? getMinutesSince(deviceStatus.createdAt)
    : Number.MAX_SAFE_INTEGER;

  const projectInfo = getProjectInfoOfIresDevice(iresDevice, clientPanel);
  let sortTimestampSource: SortTimestampSource = SortTimestampSource.NO_INFO;

  if (projectInfo?.currentProjectAssignmentTimeRange?.startDate) {
    sortTimestamp = getMinutesSince(
      projectInfo.currentProjectAssignmentTimeRange.startDate
    );

    sortTimestampSource = SortTimestampSource.DEVICE_IN_USAGE;

    if (
      matchesIresDeviceFilterWithContext(
        DeviceFilter.ALL_ACTIVE_DEVICES,
        iresDevice,
        activeUser,
        context
      )
    ) {
      sortTimestampSource = SortTimestampSource.ACTIVE_DEVICE;
    }
  } else if (deviceStatus && programChain) {
    if (isActiveProgramChainOfIresDevice(iresDevice, programChain)) {
      sortTimestamp = getMinutesSince(programChain.startDate);
      sortTimestampSource = SortTimestampSource.ACTIVE_DEVICE;
    } else {
      const isInUsage = matchesIresDeviceFilterWithContext(
        DeviceFilter.IN_USAGE,
        iresDevice,
        activeUser,
        context
      );

      if (isInUsage) {
        if (projectInfo?.currentProjectAssignmentTimeRange?.startDate) {
          sortTimestamp = getMinutesSince(
            projectInfo.currentProjectAssignmentTimeRange.startDate
          );
          sortTimestampSource = SortTimestampSource.DEVICE_IN_USAGE;
        } else {
          sortTimestamp = getMinutesSince(programChain.startDate);

          if (isActiveProgramChainOfIresDevice(iresDevice, programChain)) {
            sortTimestampSource = SortTimestampSource.DEVICE_IN_USAGE;
          } else {
            sortTimestamp = getMinutesSince(
              getEndDateOfProgramChain(programChain)
            );

            sortTimestampSource = SortTimestampSource.COMPLETED_PROGRAM_CHAIN;
          }
        }
      } else {
        if (programChain) {
          sortTimestamp = getMinutesSince(
            getEndDateOfProgramChain(programChain)
          );
        } else {
          sortTimestamp = getMinutesSince(deviceStatus.createdAt);
        }

        sortTimestampSource = SortTimestampSource.LAST_INFO;
      }
    }
  } else if (deviceStatus) {
    sortTimestamp = getMinutesSince(deviceStatus.createdAt);
    sortTimestampSource = SortTimestampSource.LAST_INFO;

    const iresDeviceCategory = getIresDeviceCategory(iresDevice);

    if (iresDeviceCategory === IresDeviceCategory.COMBOX) {
      const comBox = iresDevice as ComBox;
      const comBoxStatus = comBox.comBoxStatus;

      if (comBoxStatus.dryRunActive) {
        sortTimestamp = getMinutesSince(comBoxStatus.dryRunStartDate);
        sortTimestampSource = SortTimestampSource.ACTIVE_DEVICE;
      }
    }
  } else if (programChain) {
    sortTimestamp = getMinutesSince(getEndDateOfProgramChain(programChain));
    sortTimestampSource = SortTimestampSource.COMPLETED_PROGRAM_CHAIN;
  }

  const deviceSortData: DeviceSortData = {
    iresDevice: iresDevice,
    serialNumber: iresDevice.serialNo,
    inventoryNumber: clientPanel?.inventoryNumber ?? undefined,
    currentProjectName: projectInfo?.projectName ?? undefined,
    iresDeviceIpuState: getIresDeviceIpuState(iresDevice),
    sortTimestamp: sortTimestamp,
    sortTimestampSource: sortTimestampSource,
  };

  return deviceSortData;
}
